<div class="df-page">
  <app-project-nav></app-project-nav>
  <app-page-header
    [eyebrow]="defect()?.key ?? ''"
    [title]="defect()?.title ?? ('defect.detail.titleFallback' | translate)"
  >
    <ng-container right>
      <a mat-stroked-button [routerLink]="['/projects', projectId, 'defects']">
        <mat-icon aria-hidden="true">arrow_back</mat-icon>
        {{ 'defect.detail.backToList' | translate }}
      </a>
    </ng-container>
  </app-page-header>

  @if (errorText(); as message) {
    <div class="df-hint df-hint--error df-alert" role="alert" data-testid="defect-update-error">
      <p>{{ message }}</p>
      @if (versionConflict()) {
        <button mat-stroked-button type="button" (click)="load()">{{ 'defect.detail.reload' | translate }}</button>
      }
    </div>
  }

  @switch (state()) {
    @case ('loading') {
      <div class="df-loading" data-testid="defect-loading">
        <mat-spinner diameter="32" />
      </div>
    }
    @case ('notFound') {
      <p class="df-hint" data-testid="defect-not-found">{{ 'defect.detail.notFound' | translate }}</p>
    }
    @case ('error') {
      <div class="df-hint df-hint--error" role="alert" data-testid="defect-load-error">
        <p>{{ 'defect.detail.loadError' | translate }}</p>
        <button mat-stroked-button type="button" data-testid="defect-retry" (click)="load()">
          {{ 'defect.detail.retry' | translate }}
        </button>
      </div>
    }
    @case ('ok') {
      @if (defect(); as d) {
        <section class="df-card df-section" [attr.aria-label]="'defect.detail.overview' | translate">
          <div class="df-fields">
            <mat-form-field appearance="fill" floatLabel="always" class="df-field">
              <mat-label>{{ 'defect.detail.status' | translate }}</mat-label>
              <select
                #statusEl
                matNativeControl
                data-testid="defect-status"
                [disabled]="statusLocked()"
                (change)="changeStatus(statusEl)"
              >
                @for (status of statusChoices(); track status) {
                  <option [value]="status" [selected]="status === d.status">
                    {{ 'defect.status.' + status | translate }}
                  </option>
                }
              </select>
            </mat-form-field>
            <app-person-select
              class="df-field"
              data-testid="defect-assignee"
              [formControl]="assigneeControl"
              [label]="'defect.detail.assignee' | translate"
              [options]="personOptions()"
              [allowNone]="true"
              noneKey="defect.list.unassigned"
            />
            <dl class="df-facts">
              <div>
                <dt>{{ 'defect.detail.severity' | translate }}</dt>
                <dd>
                  <span class="df-severity" [class]="'df-severity--' + d.severity.toLowerCase()">
                    {{ 'defect.severity.' + d.severity | translate }}
                  </span>
                  @if (d.blocking) {
                    <span class="df-blocking">{{ 'defect.list.blocking' | translate }}</span>
                  }
                </dd>
              </div>
              <div>
                <dt>{{ 'defect.detail.createdBy' | translate }}</dt>
                <dd>{{ d.createdBy?.name ?? '–' }} · {{ d.createdAt | date: 'dd.MM.yyyy HH:mm' }}</dd>
              </div>
            </dl>
          </div>
          @if (d.description) {
            <div class="df-description">
              <h2 class="df-section__title">{{ 'defect.detail.description' | translate }}</h2>
              <p>{{ d.description }}</p>
            </div>
          }
        </section>

        <section class="df-card df-section" data-testid="defect-found-in" aria-labelledby="df-found-title">
          <h2 id="df-found-title" class="df-section__title">{{ 'defect.detail.foundIn' | translate }}</h2>
          <dl class="df-facts">
            <div>
              <dt>{{ 'defect.detail.testRun' | translate }}</dt>
              <dd>
                @if (d.foundIn?.testRunId) {
                  <a [routerLink]="['/projects', projectId, 'test-results', d.foundIn!.testRunId]">
                    {{ d.foundIn!.testRunKey }}
                  </a>
                } @else if (d.foundIn?.testRunKey) {
                  {{ d.foundIn!.testRunKey }}
                } @else {
                  <span class="df-missing" data-testid="defect-missing">–</span>
                }
              </dd>
            </div>
            <div>
              <dt>{{ 'defect.detail.testCase' | translate }}</dt>
              <dd>
                @if (d.foundIn?.testCaseId) {
                  <a [routerLink]="['/projects', projectId, 'test-cases', d.foundIn!.testCaseId, 'edit']">
                    {{ d.foundIn!.testCaseKey }}
                  </a>
                  {{ d.foundIn!.testCaseTitle }}
                } @else if (d.foundIn?.testCaseKey) {
                  {{ d.foundIn!.testCaseKey }} {{ d.foundIn!.testCaseTitle }}
                } @else {
                  <span class="df-missing" data-testid="defect-missing">–</span>
                }
              </dd>
            </div>
            <div>
              <dt>{{ 'defect.detail.step' | translate }}</dt>
              <dd>
                @if (d.foundIn?.stepNumber) {
                  {{ d.foundIn!.stepNumber }}. {{ d.foundIn!.stepText }}
                } @else {
                  <span class="df-missing" data-testid="defect-missing">–</span>
                }
              </dd>
            </div>
            <div>
              <dt>{{ 'defect.detail.release' | translate }}</dt>
              <dd>
                @if (d.foundIn?.releaseId) {
                  {{ d.foundIn!.releaseId }}
                } @else {
                  <span class="df-missing" data-testid="defect-missing">–</span>
                }
              </dd>
            </div>
            @if (d.verifiedIn; as verified) {
              <div data-testid="defect-verified-in">
                <dt>{{ 'defect.detail.verifiedIn' | translate }}</dt>
                <dd>{{ verified.testRunKey ?? '–' }}</dd>
              </div>
            }
          </dl>
        </section>

        <section class="df-card df-section" aria-labelledby="df-evidence-title">
          <h2 id="df-evidence-title" class="df-section__title">{{ 'defect.detail.evidence' | translate }}</h2>
          @if (d.evidence.length === 0) {
            <p class="df-missing" data-testid="defect-evidence-empty">{{ 'defect.detail.noEvidence' | translate }}</p>
          } @else {
            <ul class="df-evidence">
              @for (file of d.evidence; track file.attachmentId) {
                <li>
                  <button mat-button type="button" data-testid="defect-evidence" (click)="download(file)">
                    <mat-icon aria-hidden="true">attach_file</mat-icon>
                    {{ file.originalFilename }}
                  </button>
                </li>
              }
            </ul>
          }
        </section>
      }
    }
  }
</div>
